import { useEffect, useRef, useState } from 'react';
import { getIdToken } from '../../../services/firebase-auth.js';
import { getKommoHistoryStatus, syncKommoHistoryPage, downloadKommoHistory } from '../../../services/kommo-history-api.js';

/**
 * Tres operaciones independientes:
 * 1. Exportar DIRECTO desde Kommo, sin almacenamiento previo.
 * 2. Sincronizar Kommo -> Firestore, lectura de Kommo sin modificaciones.
 * 3. Exportar el snapshot Firestore, una vez completada su carga inicial.
 */
export default function KommoExportPanel() {
  const [status, setStatus] = useState(null);
  const [scope, setScope] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const cancelled = useRef(false);
  const [batchCount, setBatchCount] = useState(0);
  const snapshotReady = status?.leadsCompleted === true && status?.eventsCompleted === true;
  const card = { border: '1px solid #ddd', borderRadius: 10, padding: 16, marginTop: 12 };

  useEffect(() => {
    let current = true;
    getIdToken(false).then(token => getKommoHistoryStatus(token))
      .then(value => { if (current) setStatus(value); })
      .catch(err => { if (current) setError('No se pudo consultar el estado de Firestore: ' + err.message); });
    return () => { current = false; cancelled.current = true; };
  }, []);

  function selectedFilter() {
    if (scope === 'created_between' && (!from || !to || from > to)) {
      throw new Error('Seleccioná un rango Desde/Hasta válido.');
    }
    return { scope, from, to };
  }

  async function download(source, requestedFormat) {
    setBusy(source); setError(''); setMessage('');
    try {
      const filter = selectedFilter();
      const token = await getIdToken(false);
      setMessage(source === 'kommo'
        ? 'Consultando en vivo los leads y eventos de Kommo. Puede demorar varios minutos; no cierres esta pestaña.'
        : 'Preparando los CSV desde la copia de Firestore.');
      const formats = requestedFormat ? [requestedFormat] : ['stages', 'leads'];
      for (const format of formats) {
        await downloadKommoHistory(token, format, filter, source);
      }
      setMessage('Descarga completada: ' + formats.map(f => f === 'stages' ? 'historial de etapas' : 'leads').join(' y ') + ' desde ' +
        (source === 'kommo' ? 'Kommo directo.' : 'Firestore.'));
    } catch (err) {
      setError(err.message);
      setMessage('');
    } finally { setBusy(''); }
  }

  async function sync() {
    cancelled.current = false;
    setBusy('sync'); setError(''); setMessage('Sincronizando datos de Kommo con Firestore…'); setBatchCount(0);
    try {
      let data = status;
      // Reanudable: se conservan cursores en Firestore. Respetar rate limit API.
      for (let i = 0; i < 200 && !cancelled.current; i++) {
        data = await syncKommoHistoryPage(await getIdToken(false));
        setStatus(data); setBatchCount(i + 1);
        if (data.leadsCompleted && data.eventsCompleted) break;
        setMessage('Copiando lotes a Firestore; mantené esta pestaña abierta.');
        await new Promise(resolve => setTimeout(resolve, 25000));
      }
      setMessage(data?.leadsCompleted && data?.eventsCompleted
        ? 'Carga inicial de Firestore finalizada. Ya podés exportar desde nuestra base.'
        : 'Sincronización pausada. Podés reanudarla sin perder el avance.');
    } catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  return <section className="analytics-section" style={{ marginBottom: 20 }}>
    <h2>Exportación y sincronización de Kommo</h2>
    <p>Elegí si querés consultar Kommo ahora o utilizar la copia guardada en GI Capital. Son operaciones separadas.</p>

    <div style={{ ...card, marginTop: 0 }}>
      <h3>Seleccionar leads</h3>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <label><input type="radio" checked={scope === 'all'} disabled={!!busy} onChange={() => setScope('all')} /> Todos los leads</label>
        <label><input type="radio" checked={scope === 'created_between'} disabled={!!busy} onChange={() => setScope('created_between')} /> Por fecha de creación</label>
      </div>
      {scope === 'created_between' && <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        <label>Desde <input aria-label="Desde creación" className="analytics-input" type="date" value={from} disabled={!!busy} onChange={e => setFrom(e.target.value)} /></label>
        <label>Hasta <input aria-label="Hasta creación" className="analytics-input" type="date" value={to} disabled={!!busy} onChange={e => setTo(e.target.value)} /></label>
      </div>}
      <p><small>El rango filtra por fecha de creación del lead en Chile, no por fecha de cada cambio de etapa.</small></p>
    </div>

    <div style={card}>
      <h3>1. Exportar directamente desde Kommo</h3>
      <p>Consulta los leads y su historial disponible en Kommo en este momento. <strong>No requiere sincronizar</strong> ni modifica Firestore.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="analytics-btn" disabled={!!busy} onClick={() => download('kommo', 'leads')}>
          {busy === 'kommo' ? 'Consultando…' : 'Descargar leads de Kommo'}
        </button>
        <button className="analytics-btn" disabled={!!busy} onClick={() => download('kommo', 'stages')}>
          {busy === 'kommo' ? 'Consultando…' : 'Descargar historial de Kommo'}
        </button>
      </div>
      <p><small>Podés obtener leads e historial por separado, sin esperar a que termine el archivo más grande. La exportación directa puede demorar y tiene límites de volumen; nunca se presenta como completo un archivo truncado.</small></p>
    </div>

    <div style={card}>
      <h3>2. Sincronizar Kommo con nuestra base</h3>
      <p>Copia leads y eventos a Firestore para consultas, análisis e integraciones futuras. No modifica Kommo ni el CRM.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="analytics-btn" disabled={!!busy || !status || (snapshotReady && status.meetingDatesComputed)} onClick={sync}>
          {busy === 'sync' ? 'Sincronizando…' : snapshotReady ? 'Recalcular fechas de Reunión' : 'Sincronizar automáticamente'}
        </button>
        <button className="analytics-btn" disabled={busy !== 'sync'} onClick={() => { cancelled.current = true; }}>Detener después del lote</button>
      </div>
      {busy === 'sync' && <p role="status">Lotes procesados en esta sesión: {batchCount}</p>}
      {status && <p>Firestore: {status.leadsCount ?? '—'} leads · {status.eventsCount ?? '—'} eventos.
        {' '}Carga inicial: {snapshotReady ? 'finalizada' : 'pendiente'}.
        {' '}Reunión resumida: {status.meetingDatesComputed ? 'Sí' : 'No verificada'}.
        {' '}Actualizado: {status.updatedAt || 'sin fecha'}.</p>}
      {!status && <p><small>Estado Firestore no disponible. La exportación directa desde Kommo sigue siendo independiente.</small></p>}
    </div>

    <div style={card}>
      <h3>3. Exportar desde nuestra base (Firestore)</h3>
      <p>Descarga los leads y eventos que ya fueron almacenados. No vuelve a consultar los registros de Kommo para exportarlos.</p>
      <button className="analytics-btn" disabled={!!busy || !snapshotReady} onClick={() => download('firestore')}>
        {busy === 'firestore' ? 'Preparando archivos…' : 'Descargar desde Firestore'}
      </button>
      {!snapshotReady && <p><small>Este botón se habilita al finalizar la carga inicial de leads y eventos. Para descargar sin esperar, usá la opción 1.</small></p>}
    </div>

    <p><small>El historial depende de los eventos disponibles en Kommo. La carga completa de páginas no certifica cobertura de todos los años ni sincronización continua.</small></p>
    {message && <p role="status">{message}</p>}
    {error && <p className="analytics-error-inline" role="alert">{error}</p>}
  </section>;
}
